import { Stack } from "PFComponents/containers";
import { Modal } from "PFComponents/modal";
import { SelectV2 } from "PFComponents/select_v2";
import { Typography } from "PFComponents/typography";
import { fetchEngagementBlueprints } from "PFCore/services/engagement_blueprints";
import { Collection, EngagementBlueprint, Id } from "PFTypes";
import { FetchOptionsResponse, Option } from "PFTypes/options";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

type ApplyEngagementBlueprintModalProps = {
  onClose: () => void;
  activityId: Id;
};

const formatOptions = (
  response: Collection<EngagementBlueprint[]>
): FetchOptionsResponse<EngagementBlueprint> => ({
  ...response,
  entries: response.entries.map((blueprint) => ({
    id: blueprint.id,
    value: blueprint.name,
    displayElement: blueprint.name,
    original: blueprint
  }))
});

export const ApplyEngagementBlueprintModal = ({
  onClose,
  activityId
}: ApplyEngagementBlueprintModalProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "engagementBlueprint" });
  const navigate = useNavigate();
  const { t: tTranslation } = useTranslation("translation");
  const { t: tFilters } = useTranslation("filters");

  const [selectedBlueprint, setSelectedBlueprint] = useState<EngagementBlueprint | undefined>(undefined);

  const handleApply = () => {
    if (selectedBlueprint) {
      navigate(`/activities/${activityId}/engagement_blueprint/${selectedBlueprint.id}`);
    }
  };

  return (
    <Modal
      title={t("applyEngagementBlueprint")}
      onClose={onClose}
      labelOK={tFilters("apply")}
      labelCancel={tTranslation("cancel")}
      onOK={handleApply}
      disableOKButton={!selectedBlueprint}
    >
      <Stack gap="spacingLg">
        <SelectV2<EngagementBlueprint>
          label={t("engagementBlueprintLabel")}
          value={selectedBlueprint?.id}
          fetchOptions={({ ids, searchTerm }) => {
            if (ids?.length) {
              return Promise.resolve(formatOptions({ entries: [selectedBlueprint!], meta: { total: 1 } }));
            }
            return fetchEngagementBlueprints({
              page: 1,
              perPage: 50,
              ...(searchTerm ? { term: searchTerm } : {})
            }).then(formatOptions);
          }}
          onChange={(option: Option<EngagementBlueprint>) => setSelectedBlueprint(option.original)}
          isFetchingSelectedOptions={false}
        />
        <div>
          <Typography variant="labelBold">{t("pleaseNote")}</Typography>
          <Typography variant="bodyRegular">{t("applyEngagementBlueprintNote")}</Typography>
        </div>
      </Stack>
    </Modal>
  );
};
